import { useRef, useState, useEffect, useMemo, useCallback, Component, type ReactNode } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { useTexture } from '@react-three/drei'
import * as THREE from 'three'
import { useTheme } from './theme-provider'

import welephantLogo from '@/assets/tokens/welephant-white.svg'

class WebGLErrorBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false }
  static getDerivedStateFromError() { return { hasError: true } }
  render() { return this.state.hasError ? this.props.fallback : this.props.children }
}

interface CoinMeshProps {
  isSpinning: boolean
  onClick: () => void
}

// Create ridges around the edge
function CoinRidges({ radius, thickness, ridgeCount }: { radius: number; thickness: number; ridgeCount: number }) {
  const ridges = useMemo(() => {
    const ridgeGeometries: JSX.Element[] = []
    const ridgeWidth = 0.02
    const ridgeDepth = 0.015

    for (let i = 0; i < ridgeCount; i++) {
      const angle = (i / ridgeCount) * Math.PI * 2
      const x = Math.cos(angle) * radius
      const z = Math.sin(angle) * radius

      ridgeGeometries.push(
        <mesh
          key={i}
          position={[x, 0, z]}
          rotation={[0, -angle + Math.PI / 2, 0]}
        >
          <boxGeometry args={[ridgeWidth, thickness * 0.8, ridgeDepth]} />
          <meshStandardMaterial color="#4569ef" metalness={0.6} roughness={0.3} />
        </mesh>
      )
    }

    return ridgeGeometries
  }, [radius, thickness, ridgeCount])

  return <group>{ridges}</group>
}

function CoinMesh({ isSpinning, onClick }: CoinMeshProps) {
  const groupRef = useRef<THREE.Group>(null)
  const meshRef = useRef<THREE.Mesh>(null)

  // Animation state
  const animTimeRef = useRef(0)
  const spinSpeedRef = useRef(8) // Initial spin speed (radians per second)

  // Load texture
  const texture = useTexture(welephantLogo)

  // Configure texture
  useEffect(() => {
    if (texture) {
      texture.colorSpace = THREE.SRGBColorSpace
      texture.center.set(0.5, 0.5)
      texture.needsUpdate = true
    }
  }, [texture])

  // Speed up when clicked
  useEffect(() => {
    if (isSpinning) {
      spinSpeedRef.current = 8 // Reset to fast spin
    }
  }, [isSpinning])

  // Create materials for the coin (side, top cap, bottom cap)
  const materials = useMemo(() => {
    // Side material (edge of coin) - blue to match logo
    const sideMaterial = new THREE.MeshStandardMaterial({
      color: '#4569ef'
      //metalness: 0.6,
      //roughness: 0.3,
    })

    // Top face material with texture - rotate to appear right side up
    const topTexture = texture.clone()
    topTexture.center.set(0.5, 0.5)
    topTexture.rotation = Math.PI / 2  // Rotate 90 degrees to be right side up
    topTexture.needsUpdate = true

    const topMaterial = new THREE.MeshBasicMaterial({
      map: topTexture,
      color: '#4569ef',
      transparent: true,
      opacity: 1.0
    })

    // Bottom face material with texture - mirrored so it appears right side up when flipped
    const bottomTexture = texture.clone()
    bottomTexture.center.set(0.5, 0.5)
    bottomTexture.rotation = Math.PI / 2  // Same rotation as top
   //bottomTexture.wrapS = THREE.RepeatWrapping
    bottomTexture.repeat.x = -1  // Mirror horizontally
    bottomTexture.needsUpdate = true

    const bottomMaterial = new THREE.MeshStandardMaterial({
      map: bottomTexture,
      color: '#4569ef',
      transparent: true,
      opacity: 1.0
    })

    // Order: side, top, bottom
    return [sideMaterial, topMaterial, bottomMaterial]
  }, [texture])

  // Animation loop - simple spin with deceleration
  useFrame((_, delta) => {
    if (!groupRef.current) return

    // Coin always faces camera
    groupRef.current.rotation.x = Math.PI / 2
    groupRef.current.rotation.y = 0

    // Spin around Z axis and gradually decelerate
    groupRef.current.rotation.z += spinSpeedRef.current * delta

    // Decelerate spin speed (minimum 0.3 for slow idle spin)
    if (spinSpeedRef.current > 0.3) {
      spinSpeedRef.current *= 0.995
    }
  })

  const coinRadius = 1.5  // 1.5x bigger
  const coinThickness = 0.2
  const ridgeCount = 150

  return (
    <group
      ref={groupRef}
      onClick={onClick}
      onPointerOver={() => { document.body.style.cursor = 'pointer' }}
      onPointerOut={() => { document.body.style.cursor = 'default' }}
    >
      {/* Main coin body - single mesh with multi-material */}
      <mesh ref={meshRef} material={materials}>
        <cylinderGeometry args={[coinRadius, coinRadius, coinThickness, 64]} />
      </mesh>
      
    </group>
  )
}

function Scene({ isSpinning, onClick }: CoinMeshProps) {
  const { theme } = useTheme()

  // Resolve "system" theme to actual value
  const resolvedTheme = theme === 'system'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme
  const isDark = resolvedTheme === 'dark'

  return (
    <>
      {/* Ambient light - stronger in light mode */}
      <ambientLight intensity={isDark ? 0.3 : 0.5} />

      {/* Main directional light with shadow */}
      <directionalLight
        position={[5, 5, 5]}
        intensity={isDark ? 1.2 : 1.5}
        castShadow
        shadow-mapSize={[1024, 1024]}
        shadow-camera-far={50}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
      />

      {/* Fill light from opposite side */}
      <directionalLight
        position={[-3, 2, -3]}
        intensity={isDark ? 0.3 : 0.4}
      />

      {/* Rim light for metallic highlights */}
      <pointLight
        position={[0, -3, 2]}
        intensity={isDark ? 0.5 : 0.3}
        color="#6b8cff"
      />

      {/* The coin */}
      <CoinMesh
        isSpinning={isSpinning}
        onClick={onClick}
      />

      {/* Shadow receiver plane */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, -1.5, 0]}
        receiveShadow
      >
        <planeGeometry args={[10, 10]} />
        <shadowMaterial opacity={isDark ? 0.3 : 0.15} />
      </mesh>
    </>
  )
}

interface Coin3DProps {
  className?: string
}

function StaticCoinFallback({ className }: { className?: string }) {
  return (
    <div className={className} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <img src="/assets/images/welephant-1024.png" alt="WELEPHANT" style={{ width: '60%', height: '60%', objectFit: 'contain' }} />
    </div>
  )
}

export function Coin3D({ className }: Coin3DProps) {
  const [isSpinning, setIsSpinning] = useState(true)
  const [webglSupported, setWebglSupported] = useState(true)

  useEffect(() => {
    try {
      const canvas = document.createElement('canvas')
      const gl = canvas.getContext('webgl2') || canvas.getContext('webgl')
      if (!gl) setWebglSupported(false)
    } catch {
      setWebglSupported(false)
    }
  }, [])

  const handleClick = useCallback(() => {
    setIsSpinning(false)
    setTimeout(() => setIsSpinning(true), 10)
  }, [])

  if (!webglSupported) return <StaticCoinFallback className={className} />

  return (
    <div className={className} style={{ outline: 'none' }} tabIndex={-1}>
      <WebGLErrorBoundary fallback={<StaticCoinFallback className={className} />}>
        <Canvas
          shadows
          camera={{ position: [0, 0, 5], fov: 45 }}
          gl={{ antialias: true, alpha: true }}
          style={{ background: 'transparent', outline: 'none' }}
          tabIndex={-1}
        >
          <Scene
            isSpinning={isSpinning}
            onClick={handleClick}
          />
        </Canvas>
      </WebGLErrorBoundary>
    </div>
  )
}

export default Coin3D
